import axios from 'axios';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { parseNumericId } from '@/shared/converters/commonConverters';
import { DateTimeFormat, formatMoment } from '@/shared/converters/dateConverters';
import { BrandId } from '@/shared/models/Brand';
import User from '@/shared/models/User';
import { Url } from '@/shared/models/common';
import { present } from '@/shared/utils/typescript';
import { VehicleId } from '@/vehicle/models/Vehicle';
import { parseTireContainer, formatTire, formatMeasurementData, AxleData, formatAxleLayout } from '../converters/tireConverters';
import { parseTireMakes } from '../converters/tireMakeConverters';
import { parseTiresSummary } from '../converters/tiresSummaryConverters';
import { AxleId } from '../models/Axle';
import AxlesData from '../models/AxleFormData';
import Tire, { EANSearchOption, TireId } from '../models/Tire';
import TireContainer, { RepairKitData } from '../models/TireContainer';
import TireMake from '../models/TireMake';
import TireNote, { TireNoteId } from '../models/TireNote';
import { TirePositionId } from '../models/TirePosition';
import TiresSummary from '../models/TiresSummary';

export default class TireClient {
  static async loadSummary(dossierId: DossierId): Promise<TiresSummary> {
    const response = await axios.get(`api/mobopro/v1/dossiers/${dossierId}/wheels/summary`);
    return present(parseTiresSummary(response.data));
  }

  static async loadAll(dossierId: DossierId): Promise<TireContainer> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/wheels`);
    return present(parseTireContainer(response.data));
  }

  static async searchEAN(dossierId: DossierId, tire: Tire, brandId: BrandId): Promise<Array<EANSearchOption>> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/wheels/tirecatalogue`, {
      ...tire,
      brandId,
    });
    return response.data.map((i: any) => i as EANSearchOption);
  }

  static async updateAxleLayout(dossierId: DossierId, data: Array<AxleData>): Promise<void> {
    await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/wheels/settings`, formatAxleLayout(data));
  }

  static async removeAxle(dossierId: DossierId, axleId: AxleId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}/wheels/settings/${axleId}`);
  }

  static async updateMeasurements(dossierId: DossierId, data: AxlesData, modifiedBy: User): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/wheels`, formatMeasurementData(data, modifiedBy));
  }

  static async editTire(dossierId: DossierId, tire: Tire, isReplacement: boolean, isMounted: boolean, positionIds: Array<TirePositionId>): Promise<TireContainer | null> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/wheels/tires`, {
      isReplacement,
      isMounted,
      isNew: tire.isNew,
      positionIds,
      tire: formatTire(tire),
    });

    return parseTireContainer(response.data);
  }

  static async updateRepairKit(vehicleId: VehicleId, repairKit: RepairKitData): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dealerobjects/${vehicleId}`, {
      hasTireRepairKit: repairKit.present,
      tireRepairKitExpiryDate: formatMoment(repairKit.expiryDate, DateTimeFormat.DATE),
    });
  }

  static async swap(dossierId: DossierId): Promise<void> {
    await axios.put(`api/mobopro/v1/dossiers/${dossierId}/wheels/tireswap`);
  }

  static async loadMakes(): Promise<Array<TireMake>> {
    const response = await axios.get('/api/mobopro/v1/tiremakes');
    return parseTireMakes(response.data);
  }

  static async removeTire(dossierId: DossierId, id: TireId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}/wheels/tires/${id}`);
  }

  static async addBrakeNote(dossierId: DossierId, positionId: TirePositionId, content: string): Promise<TireNote> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/wheels/brakes/${positionId}/notes`, { content });
    return new TireNote(present(parseNumericId<TireNoteId>(response.data.id)), content);
  }

  static async updateBrakeNote(dossierId: DossierId, positionId: TirePositionId, noteId: TireNoteId, content: string): Promise<void> {
    await axios.put(`/api/mobopro/v1/dossiers/${dossierId}/wheels/brakes/${positionId}/notes/${noteId}`, { content });
  }

  static async removeBrakeNote(dossierId: DossierId, positionId: TirePositionId, noteId: TireNoteId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}/wheels/brakes/${positionId}/notes/${noteId}`);
  }

  static async addTireNote(dossierId: DossierId, tireId: TireId, content: string): Promise<TireNote> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/wheels/tires/${tireId}/notes`, { content });
    return new TireNote(present(parseNumericId<TireNoteId>(response.data.id)), content);
  }

  static async updateTireNote(dossierId: DossierId, tireId: TireId, noteId: TireNoteId, content: string): Promise<void> {
    await axios.put(`/api/mobopro/v1/dossiers/${dossierId}/wheels/tires/${tireId}/notes/${noteId}`, { content });
  }

  static async removeTireNote(dossierId: DossierId, tireId: TireId, noteId: TireNoteId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}/wheels/tires/${tireId}/notes/${noteId}`);
  }

  static async getMiniPortalUrl(dossierId: DossierId): Promise<Url> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/wheels/directaccessportal`);
    return response.data.portalURL;
  }

  static async getNewTireInfoStatus(dossierId: DossierId): Promise<boolean> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/wheels/directaccesstires`);
    return response.data.reload;
  }
}
